import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Provider } from 'types/provider';
import { Button, Header, Segment, Message, Icon } from 'semantic';
import { request } from 'utils/api';
import { EditSignInButton } from './EditSignInButton';

import ModalTriggerButton from 'components/modal/TriggerButton';

import { SigninButton } from 'types/provider';
import Divider from 'components/Sidebar/Divider';
import { Stack, Group } from 'components';

import ErrorMessage from 'components/ErrorMessage';
import { ActionButtonWithIcon } from 'components/semantic/ActionButtonWithIcon';
import SigninButtonComponent from 'components/SigninButton';
import { useToast } from 'components/Toast';

export default function SignInButtons({ provider }: { provider?: Provider }) {
  const { t } = useTranslation();
  const toast = useToast();

  const [modified, setModified] = useState(false);
  const [buttons, setButtons] = useState<SigninButton[]>(
    provider?.signinButtons || []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [socialLoginIssuersIds, setSocialLoginIssuersId] = useState<string[]>(
    []
  );
  const [ssoAccounts, setSSOAccounts] = useState<
    { name: string; _id: string }[]
  >([]);

  function moveItem(button: SigninButton, direction: 'up' | 'down') {
    const sorted = [...buttons];
    const index = sorted.findIndex((b) => b === button);
    if (direction === 'up' && index > 0) {
      [sorted[index], sorted[index - 1]] = [sorted[index - 1], sorted[index]];
      setButtons(sorted);
      setModified(true);
    } else if (direction === 'down' && index < sorted.length - 1) {
      [sorted[index], sorted[index + 1]] = [sorted[index + 1], sorted[index]];
      setButtons(sorted);
      setModified(true);
    }
  }

  async function fetchSocialLoginIssuers(providerId: string) {
    try {
      const { data } = await request({
        path: `/1/providers/${providerId}/configuration`,
        method: 'GET',
      });
      setSocialLoginIssuersId(
        data.socialLoginIssuers.map((issuer: any) => issuer.issuer)
      );
    } catch (e) {
      setError(error as Error);
    }
  }

  async function fetchSsoAccounts(providerId: string) {
    try {
      const res = await request({
        method: 'GET',
        path: `/1/providers/${providerId}/sso-accounts`,
      });
      setSSOAccounts(res.data || []);
    } catch (error) {
      setError(error as Error);
    }
  }

  useEffect(() => {
    if (provider) {
      fetchSocialLoginIssuers(provider.id);
      fetchSsoAccounts(provider.id);
    }
  }, [provider]);

  async function handleSaveButton() {
    if (!provider) return;
    try {
      setLoading(true);
      // Update display priorities based on current order
      const sortedButtons = [...buttons].map((button, index) => ({
        ...button,
      }));

      await request({
        method: 'POST',
        path: `/1/providers/${provider.id}/signin-buttons`,
        body: {
          signinButtons: sortedButtons,
        },
      });

      // Update local state with new priorities
      setButtons(sortedButtons);
      setModified(false);

      toast.success(
        t(
          'provider.signinButtonsSaved',
          'Login Buttons has been saved successfully'
        )
      );
    } catch (error) {
      setError(error as Error);
    } finally {
      setLoading(false);
    }
  }

  if (!provider) {
    return <div>{t('provider.notFound', 'Provider not found')}</div>;
  }

  return (
    <Stack>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
        <Header as="h4">
          {t('provider.signinButtonsTitle', 'Sign In Buttons')}
        </Header>
        <ModalTriggerButton
          modalSize="small"
          text={t('provider.signinButtonsAddButton', 'Add Sign In Button')}
          basic
          icon="plus"
          as={ActionButtonWithIcon}
          onConfirm={handleSaveButton}
          modal={
            <EditSignInButton
              hasEmailPasswordButton={buttons.some(
                (button) => button.type === 'email-password'
              )}
              ssoAccounts={ssoAccounts}
              onChange={(newButton) => {
                setModified(true);
                setButtons((currentBtns) => [...currentBtns, newButton]);
              }}
              providerId={provider.id}
              socialLoginIssuersIds={socialLoginIssuersIds}
            />
          }
        />
      </div>

      {buttons.length === 0 && (
        <div>
          <Message info>
            {t(
              'provider.signinButtonsNoButtons',
              'No sign-in buttons configured. If nothing is configured the default login screen will be displayed.'
            )}
          </Message>
        </div>
      )}

      {buttons.length > 0 && (
        <Segment
          placeholder
          style={{
            margin: 0,
            padding: '2em',
            display: 'flex',
            flexDirection: 'column',
            gap: '1em',
            alignItems: 'center',
          }}>
          {buttons.map((button, index) => (
            <div>
              <Group
                align="center"
                spacing={32}
                style={{
                  height: '60px',
                }}>
                <div
                  style={{
                    width: '400px',
                    display: 'flex',
                  }}>
                  <SigninButtonComponent
                    style={{
                      maxWidth: '90%',
                    }}
                    external
                    provider={provider}
                    button={button}
                    onLoginClick={() => {
                      // do nothing ?
                    }}
                  />
                </div>

                <Stack
                  style={{
                    alignItems: 'center',
                  }}>
                  <Button
                    compact
                    basic
                    disabled={index === 0}
                    icon
                    size="mini"
                    onClick={() => moveItem(button, 'up')}>
                    <Icon name="caret-up" />
                  </Button>
                  <Button
                    compact
                    basic
                    disabled={index === buttons.length - 1}
                    icon
                    size="mini"
                    name="caret-down"
                    onClick={() => moveItem(button, 'down')}>
                    <Icon name="caret-down" />
                  </Button>
                </Stack>
                <Group>
                  <ModalTriggerButton
                    modalSize="small"
                    basic
                    size="tiny"
                    icon="edit"
                    modal={
                      <EditSignInButton
                        initialData={button}
                        hasEmailPasswordButton={false}
                        ssoAccounts={ssoAccounts}
                        onChange={(newButton) => {
                          setModified(true);
                          setButtons((currentBtns) => [
                            ...currentBtns.map((b) =>
                              b === button ? newButton : b
                            ),
                          ]);
                        }}
                        providerId={provider.id}
                        socialLoginIssuersIds={socialLoginIssuersIds}
                      />
                    }
                  />

                  <Button
                    basic
                    size="tiny"
                    icon="trash"
                    onClick={() => {
                      setButtons((currentBtns) =>
                        currentBtns.filter((b) => b !== button)
                      );
                      setModified(true);
                    }}
                  />
                </Group>
              </Group>
            </div>
          ))}
        </Segment>
      )}
      <Divider hidden />

      <ErrorMessage error={error} onDismiss={() => setError(null)} />
      <Button
        primary
        onClick={handleSaveButton}
        loading={loading}
        disabled={!modified}>
        {t('provider.signinButtonsSaveChanges', 'Save Changes')}
      </Button>
    </Stack>
  );
}
